import { useChartCtx } from '@weenat/client/dist/core/charts'
import { SURFACE_POWER_THRESHOLD } from '@weenat/client/dist/core/history'
import { useIntl } from '@weenat/wintl'
import { NumberValue } from 'd3-scale'

const RECT_WIDTH = 20
const RECT_HEIGHT = 16
const RECT_PADDING = 4
const RECT_PADDING_X = 6

const FONT_SIZE = 12

const COLOR = '#E05252'

type NormalizeFct = (point: number | null) => number | null | NumberValue

interface SolarRadiationThresholdLineProps {
  normalizeFct: NormalizeFct
  denormalizeFct: NormalizeFct
  variant?: 'sm' | 'lg'
}

/**
 * Displays lines and area for Solar Radiation
 */
const SolarRadiationThresholdLine = ({
  normalizeFct,
  denormalizeFct,
  variant = 'lg'
}: SolarRadiationThresholdLineProps) => {
  const { t } = useIntl()
  const { yScale, xScale, height, marginBottom } = useChartCtx()

  const [firstPointXPos, lastPointXPos] = xScale.range()
  const thresholdYPos = yScale(normalizeFct(SURFACE_POWER_THRESHOLD)!)

  const label =
    variant === 'sm'
      ? `${SURFACE_POWER_THRESHOLD}`
      : `${SURFACE_POWER_THRESHOLD} ${t('units.watts_per_square_meter_abbr')}`

  const maxNumberValue = denormalizeFct(yScale.domain()[1])
  const max = typeof maxNumberValue === 'number' ? maxNumberValue : maxNumberValue?.valueOf()

  const isThresholdInChart = (max ?? 0) > SURFACE_POWER_THRESHOLD

  let labelPosition = thresholdYPos + RECT_HEIGHT + RECT_PADDING

  const overflowsOnTimeAxis = labelPosition > height - marginBottom

  if (overflowsOnTimeAxis) {
    labelPosition = thresholdYPos - 8
  }

  return isThresholdInChart ? (
    <>
      {/* POSITIONED LABEL ON TICKS */}
      {variant === 'sm' ? (
        <>
          <line
            strokeWidth={2}
            stroke={COLOR}
            x1={firstPointXPos}
            x2={lastPointXPos}
            y1={thresholdYPos}
            y2={thresholdYPos}
          />
          <g
            transform={`translate(${firstPointXPos - RECT_WIDTH / 2},${thresholdYPos + RECT_HEIGHT / 2 - RECT_PADDING})`}
          >
            <text
              textAnchor='end'
              style={{
                fontFamily: 'Inter',
                fontSize: 10,
                fontWeight: 'bold',
                fill: COLOR
              }}
            >
              {label}
            </text>
          </g>
        </>
      ) : (
        <>
          <defs>
            <filter x='-10%' y='-10%' width='120%' height='120%' id='solid'>
              <feFlood floodColor={COLOR} result='bg' />
              <feMerge>
                <feMergeNode in='bg' />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
          </defs>

          <line
            strokeWidth={2}
            stroke={COLOR}
            x1={firstPointXPos}
            x2={lastPointXPos}
            y1={thresholdYPos}
            y2={thresholdYPos}
          />
          <g transform={`translate(${lastPointXPos - RECT_PADDING_X},${labelPosition})`}>
            <text
              textAnchor='end'
              style={{
                fontFamily: 'Inter',
                fontSize: FONT_SIZE,
                fontWeight: 'bold',
                fill: 'white'
              }}
              filter='url(#solid)'
            >
              {label}
            </text>
          </g>
        </>
      )}
    </>
  ) : null
}

export default SolarRadiationThresholdLine
